import { useContext, useEffect } from 'react';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import UserApi from '../../../rest-api/user-api';
import { isAuthExpiredError, isAuthExpiredOverrideActive } from '../../../utils/auth-error-utils';

export function useGenerateLink(handoutData, isBundled = false, whiteboardData = null) {
    const { authStore, userDataStore } = useContext(StoreContext);

    useEffect(() => {
        if (whiteboardData) {
            localStorage.setItem(
                'lastWhiteboardState',
                JSON.stringify({
                    whiteboardId: whiteboardData.whiteboardId,
                    handoutId: whiteboardData.handoutId,
                    canvasImageLink: whiteboardData.canvasImageLink,
                    canvasJsonLink: whiteboardData.canvasJsonLink,
                    fileName: whiteboardData.fileName || '',
                    eTag: whiteboardData.eTag || null,
                    ...(whiteboardData.updatedByFirstName && {
                        updatedByFirstName: whiteboardData.updatedByFirstName,
                    }),
                    ...(whiteboardData.updatedByLastName && {
                        updatedByLastName: whiteboardData.updatedByLastName,
                    }),
                    ...(whiteboardData.diagramVariant && { diagramVariant: whiteboardData.diagramVariant }),
                }),
            );
        }
    }, [whiteboardData]);

    // Process handout data to ensure it is in the correct format for the API call
    const processHandoutData = () => (isBundled ? handoutData.map((data) => data.contentId) : [handoutData.contentId]);

    // Check if we should show patient details dialog
    // Show dialog for "My Library" content (content without patient data already)
    const shouldShowPatientDialog = () => {
        const lastWhiteboardState = localStorage.getItem('lastWhiteboardState');
        const latestWhiteboardData = lastWhiteboardState ? JSON.parse(lastWhiteboardState) : null;

        const lastCustomisedHandoutState = localStorage.getItem('lastCustomisedHandoutState');
        const latestCustomisedHandoutData = lastCustomisedHandoutState ? JSON.parse(lastCustomisedHandoutState) : null;

        const isBundledContent = isBundled || Array.isArray(handoutData);
        const matchesCurrentWhiteboard =
            !isBundledContent &&
            latestWhiteboardData &&
            latestWhiteboardData.handoutId &&
            latestWhiteboardData.handoutId === handoutData.contentId;
        const matchesCurrentCustomised =
            !isBundledContent &&
            latestCustomisedHandoutData &&
            latestCustomisedHandoutData.baseContentId &&
            latestCustomisedHandoutData.baseContentId === handoutData.contentId;

        // If whiteboard/customised handout has patient data, it's from "My Clinic" - don't show dialog
        const hasWhiteboardPatientData =
            matchesCurrentWhiteboard && (latestWhiteboardData.patientName || latestWhiteboardData.patientSurname);
        const hasCustomisedPatientData =
            matchesCurrentCustomised &&
            (latestCustomisedHandoutData.patientName || latestCustomisedHandoutData.patientSurname);

        // Show dialog if there's no existing patient data (My Library)
        return !hasWhiteboardPatientData && !hasCustomisedPatientData;
    };

    const generateLink = async (patientDetailsFromDialog = null, shareContext = null) => {
        if (isAuthExpiredOverrideActive()) {
            const forcedError = new Error('Session expired (forced)');
            forcedError.isAuthExpired = true;
            throw forcedError;
        }

        const contentIds = processHandoutData();

        // Re-read the latest saved whiteboard data from localStorage
        const lastWhiteboardState = localStorage.getItem('lastWhiteboardState');
        const latestWhiteboardData = lastWhiteboardState ? JSON.parse(lastWhiteboardState) : null;

        // Check for customised handout data
        const lastCustomisedHandoutState = localStorage.getItem('lastCustomisedHandoutState');
        const latestCustomisedHandoutData = lastCustomisedHandoutState ? JSON.parse(lastCustomisedHandoutState) : null;

        const isBundledContent = isBundled || Array.isArray(handoutData);
        const matchesCurrentWhiteboard =
            !isBundledContent &&
            latestWhiteboardData &&
            latestWhiteboardData.handoutId &&
            latestWhiteboardData.handoutId === handoutData.contentId;
        const matchesCurrentCustomised =
            !isBundledContent &&
            latestCustomisedHandoutData &&
            latestCustomisedHandoutData.baseContentId &&
            latestCustomisedHandoutData.baseContentId === handoutData.contentId;

        // Priority: customised handout > whiteboard > handout
        let type = 'handout';
        if (matchesCurrentCustomised && latestCustomisedHandoutData?.customisedId) {
            type = 'customisedHandout';
        } else if (matchesCurrentWhiteboard && latestWhiteboardData?.whiteboardId) {
            type = 'whiteboard';
        }

        // Extract patient details from "My Clinic" content or use provided details from dialog
        let patientDetails = patientDetailsFromDialog;

        if (!patientDetails) {
            // Check if whiteboard or customised handout has patient data (from "My Clinic")
            if (matchesCurrentWhiteboard && (latestWhiteboardData.patientName || latestWhiteboardData.patientSurname)) {
                patientDetails = {
                    patientName: latestWhiteboardData.patientName || null,
                    patientSurname: latestWhiteboardData.patientSurname || null,
                    patientId: latestWhiteboardData.patientId || null,
                    patientEmail: latestWhiteboardData.patientEmail || null,
                    clinicPatientId: latestWhiteboardData.clinicPatientId || null,
                };
            } else if (
                matchesCurrentCustomised &&
                (latestCustomisedHandoutData.patientName || latestCustomisedHandoutData.patientSurname)
            ) {
                patientDetails = {
                    patientName: latestCustomisedHandoutData.patientName || null,
                    patientSurname: latestCustomisedHandoutData.patientSurname || null,
                    patientId: latestCustomisedHandoutData.patientId || null,
                    patientEmail: latestCustomisedHandoutData.patientEmail || null,
                    clinicPatientId: latestCustomisedHandoutData.clinicPatientId || null,
                };
            }
        }

        try {
            const currentUserFirstName = userDataStore?.userData?.firstName || null;
            const currentUserLastName = userDataStore?.userData?.lastName || null;
            const resolvedUpdatedByFirstName =
                latestWhiteboardData?.updatedByFirstName ||
                latestCustomisedHandoutData?.updatedByFirstName ||
                currentUserFirstName ||
                null;
            const resolvedUpdatedByLastName =
                latestWhiteboardData?.updatedByLastName ||
                latestCustomisedHandoutData?.updatedByLastName ||
                currentUserLastName ||
                null;

            console.warn(
                'Sharing data being sent:',
                'contentIds:',
                contentIds,
                'type:',
                type,
                'whiteboardId:',
                latestWhiteboardData?.whiteboardId || null,
                'customisedId:',
                latestCustomisedHandoutData?.customisedId || null,
                'patientDetails:',
                patientDetails,
            );

            const result = await UserApi.generatePatientLink(
                authStore.idToken,
                contentIds,
                type,
                latestWhiteboardData?.whiteboardId || null,
                latestWhiteboardData?.canvasImageLink || null,
                latestWhiteboardData?.eTag || null,
                resolvedUpdatedByFirstName,
                resolvedUpdatedByLastName,
                latestCustomisedHandoutData?.customisedId || null, // Pass customised handout ID
                patientDetails, // Pass patient details
                shareContext,
            );
            const linkId = result.linkId;
            const userId = userDataStore.userId;
            const linkWithClinicId = `${linkId}&clinicId=${userId}`;

            const dataToCache = {
                linkId,
                linkWithClinicId,
                whiteboardId: latestWhiteboardData ? latestWhiteboardData.whiteboardId : null,
                customisedId: latestCustomisedHandoutData ? latestCustomisedHandoutData.customisedId : null,
                timestamp: new Date().getTime(),
            };
            localStorage.setItem('contentLink', JSON.stringify(dataToCache));

            return linkId;
        } catch (error) {
            console.error('Error generating link:', error);
            if (isAuthExpiredError(error)) {
                throw error;
            }
            const wrappedError = new Error('Failed to generate link');
            wrappedError.originalError = error;
            wrappedError.config = error?.config;
            wrappedError.isAuthExpired = Boolean(error?.isAuthExpired);
            throw wrappedError;
        }
    };

    const generateLinkOnly = async (toggleStates = {}, patientDetailsFromDialog = null, shareContext = null) => {
        const linkId = await generateLink(patientDetailsFromDialog, shareContext);

        const userId = userDataStore.userId;
        const url = new URL(process.env.REACT_APP_PATIENT_HANDOUT_URL);

        url.searchParams.append('linkId', linkId);
        url.searchParams.append('clinicId', userId);

        // If toggles are off, add them to the URL
        const { show3DModel, showMainIllustration, showTutorialVideo } = toggleStates;
        if (show3DModel === false) url.searchParams.append('show3DModel', 'false');
        if (showMainIllustration === false) url.searchParams.append('showMainIllustration', 'false');
        if (showTutorialVideo === false) url.searchParams.append('showTutorialVideo', 'false');

        return url.toString();
    };

    const createShareData = async (toggleStates = {}, patientDetailsFromDialog = null, shareContext = null) => {
        // Use generateLinkOnly to create the full URL with toggle states and patient details
        const link = await generateLinkOnly(toggleStates, patientDetailsFromDialog, shareContext);
        const title = `Cetra Health Information Sheet(s)`;
        const handoutTitle = handoutData.contentName;

        let messageSinglePhysio = `Please read your ${handoutTitle} handout by clicking the link below. This handout contains valuable information around your condition. If you have any questions about your condition, please don't hesitate to reach out directly - We're here to help!\n\n ${link}`;

        let messageBundledPhysio = `As discussed in your consultation, these handouts contain valuable information around your condition. \n\n Please take a moment to open and read the attached handouts. If you have any questions about your condition, please don't hesitate to reach out directly to us - We're here to help! \n\n ${link}`;
        //TODO: needs removal of not neccessery things
        let messageSingle = messageSinglePhysio;
        let messageBundled = messageBundledPhysio;

        const message = isBundled ? messageBundled : messageSingle;

        return { title, url: link, message };
    };

    return { generateLink, generateLinkOnly, createShareData, shouldShowPatientDialog };
}
